import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import InfoIcon from "@mui/icons-material/Info";
import { NotInterested } from "@mui/icons-material";
import { Chip, Divider, ListItemIcon, ListItemText, Menu, MenuItem, Popover, Stack } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { visibleNavSections } from "@domain/Auth";
import { useAuthUseCases } from "@gui/contexts/useCases/UseCasesContext";
import { useAirtableStatus } from "@gui/hooks/useAirtableStatus";
import { AppVersion } from "@gui/widgets/AppVersion";
import { EntityPicker } from "@gui/widgets/EntityPicker";
import { VisuallyHidden } from "@gui/widgets/A11y";
import { ThemeModeToggle } from "@gui/widgets/ThemeModeToggle";
import { ShowTestJobsChip, ShowTestJobsToggle } from "@gui/widgets/ShowTestJobsToggle";
import { API } from "@gui/infrastructure/pass-through/api";
import { navLinks } from "@gui/routes/navigation";
import { useRouteTitle } from "@gui/routes/useRouteTitle";
import { UserContext } from "@gui/contexts/user/UserContext";
import { useCompanyContext } from "@gui/contexts/company";
import { useImpersonationContext } from "@gui/contexts/impersonation";
import { useImpersonationSelector } from "./useImpersonationSelector";
import { useResponsiveView } from "@gui/hooks/useResponsiveView";
import { darkInputSx } from "./toolbarStyles";
import { ToolbarJobSearch } from "./ToolbarJobSearch";
import { useToolbarJobSearchViewModel } from "./ToolbarJobSearch.vm";

interface ButtonAppBarProps {
	onMenuClick?: () => void;
}

// Theme palette keys (resolved by `sx`), so the dot follows the scheme like every other
// semantic status colour instead of pinning light-mode hexes.
const STATUS_COLORS: Record<string, string> = {
	none: "success.main",
	minor: "warning.main",
	major: "error.main",
	critical: "error.main",
};

// The text equivalent of STATUS_COLORS. The dot carries the entire meaning of this row in its
// hue, so without these the state is unreadable to an agent or a screen reader — and `major` and
// `critical` share a color, so it isn't fully recoverable by eye either.
const STATUS_LABELS: Record<string, string> = {
	none: "all systems operational",
	minor: "minor outage",
	major: "major outage",
	critical: "critical outage",
};

// Airtable status row for the account menu. Kept as its own component so the polling
// hook (useAirtableStatus) only mounts when the admin-only item is rendered.
function AirtableStatusMenuItem({ onClose }: { onClose: () => void }) {
	const { indicator } = useAirtableStatus();
	const color = indicator ? STATUS_COLORS[indicator] : "text.disabled";
	const label = indicator ? (STATUS_LABELS[indicator] ?? indicator) : "status unknown";
	return (
		<MenuItem
			onClick={() => {
				window.open("https://status.airtable.com", "_blank", "noopener,noreferrer");
				onClose();
			}}
		>
			<ListItemIcon>
				<Box sx={{ width: 24, display: "flex", justifyContent: "center" }}>
					<Box aria-hidden="true" sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: color }} />
				</Box>
			</ListItemIcon>
			<ListItemText>
				Airtable Status
				<VisuallyHidden>: {label}</VisuallyHidden>
			</ListItemText>
		</MenuItem>
	);
}

export function CustomToolbar(props: ButtonAppBarProps) {
	const user = useContext(UserContext);
	const title = useRouteTitle();
	const { companies, selectedCompany, setSelectedCompany } = useCompanyContext();

	const { isMobile } = useResponsiveView();
	const showCompanySelector = user?.isAdmin && companies.length > 1;
	// Who gets the ops items of the account menu, and the "Show Test Jobs" setting with its chip.
	// The API applies the same rule to that setting, so this only decides what is drawn.
	const isRogoAdmin = Boolean(user?.isAdmin && user?.isRogo);
	const { isImpersonating } = useImpersonationContext();
	const showImpersonationSelector = user?.isDeveloper || isImpersonating;
	const {
		impersonatedEmail,
		impersonatedUser,
		onImpersonationPicked,
		stopImpersonation,
		users,
		isLoading,
		fetchUsers,
	} = useImpersonationSelector(selectedCompany?.id, showImpersonationSelector);

	const [overflowAnchor, setOverflowAnchor] = useState<HTMLElement | null>(null);
	const [accountMenuAnchor, setAccountMenuAnchor] = useState<HTMLElement | null>(null);

	// Floating overlays must not survive navigation: the toolbar/sidebar never unmount
	// (persistent chrome), so open Menus stayed pinned over the next page, and a clicked
	// trigger kept its focus-tooltip open indefinitely. Close the menus and blur the
	// trigger whenever the route changes (customer-portal-oe9m).
	const location = useLocation();
	useEffect(() => {
		setOverflowAnchor(null);
		setAccountMenuAnchor(null);
		(document.activeElement as HTMLElement | null)?.blur?.();
	}, [location.pathname]);

	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const companyParam = searchParams.get("company");
	const buildNavPath = (path: string) => (companyParam ? `${path}?company=${companyParam}` : path);

	const { auth } = useAuthUseCases();
	const showLogout = visibleNavSections(user).showLogout;
	const handleLogout = () => {
		auth.logout();
		// Hard reload to /login so all in-memory caches reset alongside the cleared tokens.
		window.location.href = "/login";
		setAccountMenuAnchor(null);
	};

	const jobSearch = useToolbarJobSearchViewModel();

	// Whether there is anything to show in the toolbar controls cluster.
	const hasControls =
		jobSearch.visible || showCompanySelector || user?.isDeveloper || (showImpersonationSelector && isImpersonating);

	const companySelector = showCompanySelector && (
		<EntityPicker
			label="Company"
			size="small"
			options={companies}
			value={selectedCompany}
			onChange={setSelectedCompany}
			inputSx={isMobile ? undefined : darkInputSx}
			sx={isMobile ? { width: "100%" } : { minWidth: { xs: 150, sm: 220 }, maxWidth: 300, flexShrink: 1 }}
		/>
	);

	const impersonationChip = showImpersonationSelector && isImpersonating && (
		<Chip
			label={`Impersonating: ${impersonatedEmail}`}
			color="warning"
			size="small"
			onDelete={stopImpersonation}
			sx={{ fontWeight: "bold", ...(isMobile ? { alignSelf: "flex-start" } : { ml: 1 }) }}
		/>
	);

	const impersonationSelector = user?.isDeveloper && (
		<EntityPicker
			label="Impersonate User"
			size="small"
			options={users}
			loading={isLoading}
			value={impersonatedUser}
			getOptionLabel={(option) => `${option.name} (${option.email})`}
			isEqual={(option, value) => option.email === value.email}
			onOpen={fetchUsers}
			onChange={onImpersonationPicked}
			inputSx={isMobile ? undefined : darkInputSx}
			sx={isMobile ? { width: "100%" } : { minWidth: { xs: 150, sm: 250 }, maxWidth: 350, flexShrink: 1, ml: 1 }}
		/>
	);

	return (
		<Box sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}>
			<AppBar position="static">
				<Toolbar>
					{isMobile && (
						<IconButton
							size="large"
							edge="start"
							color="inherit"
							aria-label="menu"
							onClick={props.onMenuClick}
							sx={{ mr: 2 }}
						>
							<MenuIcon />
						</IconButton>
					)}
					{/* Rendered as a plain <div>, so it carries no heading role — `toolbar-title` is
					    what E2E anchors "am I on the right page" to. */}
					<Typography variant="h6" component="div" sx={{ flexGrow: 1 }} data-testid="toolbar-title">
						{title}
					</Typography>

					{/* Not folded into the mobile overflow menu with the selectors: this is a warning
					    that test jobs are on screen, so it has to be visible without opening anything. */}
					{isRogoAdmin && <ShowTestJobsChip compact={isMobile} />}

					{/* Desktop: render the selectors inline on the AppBar. */}
					{!isMobile && (
						<>
							{jobSearch.visible && <ToolbarJobSearch vm={jobSearch} onAppBar />}
							{companySelector}
							{impersonationChip}
							{impersonationSelector}
						</>
					)}

					{/* Mobile: collapse the selectors into an overflow menu so they don't crowd the bar. */}
					{isMobile && hasControls && (
						<>
							<IconButton
								color="inherit"
								aria-label="account settings"
								onClick={(e) => setOverflowAnchor(e.currentTarget)}
							>
								<MoreVertIcon />
							</IconButton>
							<Popover
								open={Boolean(overflowAnchor)}
								anchorEl={overflowAnchor}
								onClose={() => setOverflowAnchor(null)}
								anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
								transformOrigin={{ vertical: "top", horizontal: "right" }}
							>
								<Stack spacing={2} sx={{ p: 2, width: 280 }}>
									{jobSearch.visible && <ToolbarJobSearch vm={jobSearch} onAppBar={false} />}
									{companySelector}
									{impersonationChip}
									{impersonationSelector}
								</Stack>
							</Popover>
						</>
					)}

					{/* Account menu: holds less-used items (Profile, …) moved off the sidebar. */}
					<IconButton
						color="inherit"
						aria-label="account menu"
						data-testid="account-menu-button"
						onClick={(e) => setAccountMenuAnchor(e.currentTarget)}
						sx={{ ml: 1 }}
					>
						<PersonIcon />
					</IconButton>
					<Menu
						anchorEl={accountMenuAnchor}
						open={Boolean(accountMenuAnchor)}
						onClose={() => setAccountMenuAnchor(null)}
						anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
						transformOrigin={{ vertical: "top", horizontal: "right" }}
					>
						{navLinks("account", user).map((link) => (
							<MenuItem
								key={link.to}
								onClick={() => {
									navigate(buildNavPath(link.to), { state: { title: link.text } });
									setAccountMenuAnchor(null);
								}}
							>
								{link.icon && <ListItemIcon>{link.icon}</ListItemIcon>}
								<ListItemText>{link.text}</ListItemText>
							</MenuItem>
						))}
						{showLogout && (
							<MenuItem key="logout" onClick={handleLogout}>
								<ListItemIcon>
									<LogoutIcon />
								</ListItemIcon>
								<ListItemText>Logout</ListItemText>
							</MenuItem>
						)}
						{isRogoAdmin && [
							<Divider key="admin-divider" />,
							<MenuItem
								key="clear-cache"
								onClick={() => {
									API.Data.clearCache();
									setAccountMenuAnchor(null);
								}}
							>
								<ListItemIcon>
									<NotInterested />
								</ListItemIcon>
								<ListItemText>Clear Portal Cache</ListItemText>
							</MenuItem>,
							<AirtableStatusMenuItem key="airtable-status" onClose={() => setAccountMenuAnchor(null)} />,
							<ShowTestJobsToggle key="show-test-jobs" />,
						]}
						<Divider />
						{/* Light / Dark / System picker. Renders as three MenuItems so they slot into
						    the MenuList's native keyboard nav; selecting a mode leaves the menu open. */}
						<ThemeModeToggle />
						<Divider />
						<MenuItem disabled sx={{ "&.Mui-disabled": { opacity: 1 } }}>
							<ListItemIcon>
								<InfoIcon fontSize="small" />
							</ListItemIcon>
							<ListItemText
								primary={<AppVersion />}
								sx={{ "& .MuiListItemText-primary": { color: "text.secondary" } }}
							/>
						</MenuItem>
					</Menu>
				</Toolbar>
			</AppBar>
		</Box>
	);
}
